import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "../pages/AdminSidebar";
import "./AdminDashboard.css";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in";

const API = `${BASE_URL}/api`;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const QUARTERS = [
  {
    label: "April to June",
    startMonth: 3,
    endMonth: 5,
    yearOffset: 0,
  },
  {
    label: "July to September",
    startMonth: 6,
    endMonth: 8,
    yearOffset: 0,
  },
  {
    label: "October to December",
    startMonth: 9,
    endMonth: 11,
    yearOffset: 0,
  },
  {
    label: "January to March",
    startMonth: 0,
    endMonth: 2,
    yearOffset: 1,
  },
];

const normalizeArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.requests)) return data.requests;
  return [];
};

const getAuthToken = () => {
  let storedUser = {};

  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    storedUser = {};
  }

  const token =
    storedUser?.token ||
    storedUser?.accessToken ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("userToken") ||
    "";

  return String(token)
    .replace(/^Bearer\s+/i, "")
    .replace(/^["']|["']$/g, "")
    .trim();
};

const getAuthConfig = () => {
  const token = getAuthToken();

  return {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "Content-Type": "application/json",
    },
  };
};

const parseSafeDate = (value) => {
  if (!value) return null;

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const formatINR = (value) =>
  `₹ ${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  const date = parseSafeDate(value);

  if (!date) return "-";

  return date.toLocaleDateString("en-IN");
};

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getLeadProjectName = (lead) =>
  lead?.projectName ||
  lead?.project?.name ||
  lead?.name ||
  lead?.clientName ||
  "Unnamed Project";

const getLeadClientName = (lead) =>
  lead?.clientName || lead?.customerName || lead?.client?.name || "-";

const getLeadWonDate = (lead) =>
  parseSafeDate(lead?.wonDate) ||
  parseSafeDate(lead?.updatedAt) ||
  parseSafeDate(lead?.createdAt);

export default function AdminDashboard({ onSelectProject }) {
  const navigate = useNavigate();

  const openProject = (project) => {
    const projectId = project?._id || project?.id;
    if (!projectId) {
      alert("Project ID not found.");
      return;
    }
    // App.js owns selectedProject state. Its callback updates that state and
    // persists selectedProjectId before navigating to the existing /project route.
    if (typeof onSelectProject !== "function") {
      alert(
        "Project navigation is not connected. Pass onSelectProject from App.js.",
      );
      return;
    }
    onSelectProject(project);
    navigate("/project");
  };

  const [projects, setProjects] = useState([]);
  const [payments, setPayments] = useState([]);
  const [vendorPayments, setVendorPayments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [vendorPaymentRequests, setVendorPaymentRequests] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [showArchivedProjects, setShowArchivedProjects] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [paymentModes, setPaymentModes] = useState([]);
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const [financeFilterType, setFinanceFilterType] = useState("MONTHLY");

  const [selectedFinanceYear, setSelectedFinanceYear] = useState(currentYear);

  const [selectedFinanceMonth, setSelectedFinanceMonth] =
    useState(currentMonth);

  const [selectedQuarterIndex, setSelectedQuarterIndex] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [achievedSearch, setAchievedSearch] = useState("");

  const [darkMode, setDarkMode] = useState(false);

  const [showAchievedProjects, setShowAchievedProjects] = useState(false);

  const [requestFilter, setRequestFilter] = useState("PENDING_APPROVAL");

  const [actionLoadingId, setActionLoadingId] = useState("");

  const yearOptions = useMemo(() => {
    const years = new Set([
      2024,
      2025,
      2026,
      2027,
      2028,
      currentYear - 1,
      currentYear,
      currentYear + 1,
    ]);

    return [...years].sort((a, b) => a - b);
  }, [currentYear]);

  /* =====================================================
     LOAD DASHBOARD DATA
  ===================================================== */

  const loadAll = async () => {
    setLoading(true);
    setLoadError("");

    const authConfig = getAuthConfig();

    try {
      const [
        leadsResult,
        projectsResult,
        paymentsResult,
        vendorRequestsResult,
        vendorPaymentsResult,
      ] = await Promise.allSettled([
        axios.get(`${API}/leads`, authConfig),
        axios.get(`${API}/projects`, authConfig),
        axios.get(`${API}/payments`, authConfig),
        axios.get(`${API}/vendor-payment-requests`, authConfig),
        axios.get(`${API}/vendor-payments`, authConfig),
      ]);

      const getResultData = (result, name) => {
        if (result.status === "fulfilled") {
          return normalizeArray(result.value.data);
        }

        console.error(`${name} load error:`, {
          status: result.reason?.response?.status,
          data: result.reason?.response?.data,
          message: result.reason?.message,
        });

        return [];
      };

      const projectList = getResultData(projectsResult, "Projects");

      setLeads(getResultData(leadsResult, "Leads"));
      setProjects(projectList);

      setPayments(getResultData(paymentsResult, "Payments"));

      setVendorPayments(getResultData(vendorPaymentsResult, "Vendor payments"));

      setVendorPaymentRequests(
        getResultData(vendorRequestsResult, "Vendor payment requests").map(
          (request) => ({
            ...request,
            originalPaymentAmt: Number(request.paymentAmt || 0),
          }),
        ),
      );

      const failedResults = [
        leadsResult,
        projectsResult,
        paymentsResult,
        vendorRequestsResult,
        vendorPaymentsResult,
      ].filter((result) => result.status === "rejected");

      if (failedResults.length) {
        setLoadError(
          "Some dashboard data could not be loaded. Please check your login and API connection.",
        );
      }

      /*
       * Existing backend requires projectName for
       * GET /api/quotes.
       *
       * Fetch quotations project-wise so the
       * Projects Overview can show approved amounts.
       */

      const quoteResults = await Promise.all(
        projectList.map(async (project) => {
          const projectName = String(project?.name || "").trim();

          if (!projectName) return [];

          try {
            const response = await axios.get(`${API}/quotes`, {
              ...authConfig,
              params: {
                projectName,
              },
            });

            return normalizeArray(response.data);
          } catch (error) {
            if (error?.response?.status !== 404) {
              console.error(`Quote load error for ${projectName}:`, error);
            }

            return [];
          }
        }),
      );

      setQuotes(quoteResults.flat());
    } catch (error) {
      console.error("Dashboard load error:", error);

      setLoadError("Unable to load dashboard. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);
  useEffect(() => {
    const fetchPaymentModes = async () => {
      try {
        const response = await axios.get(
          `${API}/vendor-payment-requests/payment-modes`,
          getAuthConfig(),
        );

        setPaymentModes(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error(
          "Admin payment modes fetch error:",
          error?.response?.data || error,
        );
        setPaymentModes([]);
      }
    };

    fetchPaymentModes();
  }, []);
  const loadVendorRequests = async () => {
    try {
      const response = await axios.get(
        `${API}/vendor-payment-requests`,
        getAuthConfig(),
      );

      setVendorPaymentRequests(
        normalizeArray(response.data).map((request) => ({
          ...request,
          originalPaymentAmt: Number(request.paymentAmt || 0),
        })),
      );
    } catch (error) {
      console.error("Vendor request load error:", error);

      alert(
        error?.response?.data?.message ||
          "Unable to refresh vendor payment requests.",
      );
    }
  };

  /* =====================================================
     VENDOR APPROVAL ACTIONS
  ===================================================== */

  const updatePendingRequestField = (id, field, value) => {
    setVendorPaymentRequests((previous) =>
      previous.map((request) =>
        request._id === id
          ? {
              ...request,
              [field]: value,
            }
          : request,
      ),
    );
  };

  const approveRequest = async (id) => {
    try {
      setActionLoadingId(id);

      const requestData = vendorPaymentRequests.find(
        (request) => request._id === id,
      );

      if (!requestData) {
        alert("Payment request not found.");
        return;
      }

      const approvedAmount = Number(requestData.paymentAmt || 0);

      const approvedMode = String(requestData.mode || "").trim();

      if (!Number.isFinite(approvedAmount) || approvedAmount <= 0) {
        alert("Please enter a valid payment amount before approval.");
        return;
      }

      if (!approvedMode) {
        alert("Please select payment mode before approval.");
        return;
      }

      const otherInProcess = Math.max(
        Number(requestData.inProcessAmount || 0) -
          Number(requestData.originalPaymentAmt ?? requestData.paymentAmt ?? 0),
        0,
      );

      const updatedInProcessAmount = otherInProcess + approvedAmount;

      const updatedBalanceAmount = Math.max(
        Number(requestData.poTotal || 0) -
          Number(requestData.paidAmount || 0) -
          updatedInProcessAmount,
        0,
      );

      await axios.put(
        `${API}/vendor-payment-requests/${id}/approve`,
        {
          approvalRemark: requestData.adminRemark || "",
          paymentAmt: approvedAmount,
          mode: approvedMode,
          inProcessAmount: updatedInProcessAmount,
          balanceAmount: updatedBalanceAmount,
        },
        getAuthConfig(),
      );

      await loadVendorRequests();

      alert("Payment request approved successfully.");
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to approve request.");
    } finally {
      setActionLoadingId("");
    }
  };

  const rejectRequest = async (id) => {
    const remark = window.prompt("Enter rejection remark:", "");

    if (remark === null) return;

    try {
      setActionLoadingId(id);

      await axios.put(
        `${API}/vendor-payment-requests/${id}/reject`,
        {
          rejectRemark: remark,
        },
        getAuthConfig(),
      );

      await loadVendorRequests();

      alert("Payment request rejected successfully.");
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to reject request.");
    } finally {
      setActionLoadingId("");
    }
  };

  /* =====================================================
     FINANCE FILTER
  ===================================================== */

  const isDateInSelectedFinanceFilter = (date) => {
    if (!date) return false;

    const year = date.getFullYear();
    const month = date.getMonth();

    if (financeFilterType === "MONTHLY") {
      return year === selectedFinanceYear && month === selectedFinanceMonth;
    }

    if (financeFilterType === "QUARTERLY") {
      const quarter = QUARTERS[selectedQuarterIndex];

      const quarterYear = selectedFinanceYear + quarter.yearOffset;

      return (
        year === quarterYear &&
        month >= quarter.startMonth &&
        month <= quarter.endMonth
      );
    }

    if (financeFilterType === "YEARLY") {
      const startDate = new Date(selectedFinanceYear, 3, 1);

      const endDate = new Date(selectedFinanceYear + 1, 2, 31, 23, 59, 59);

      return date >= startDate && date <= endDate;
    }

    return false;
  };

  const selectedFilterLabel = useMemo(() => {
    if (financeFilterType === "MONTHLY") {
      return `${MONTHS[selectedFinanceMonth]} ${selectedFinanceYear}`;
    }

    if (financeFilterType === "QUARTERLY") {
      const quarter = QUARTERS[selectedQuarterIndex];

      const year = selectedFinanceYear + quarter.yearOffset;

      return `${quarter.label} ${year}`;
    }

    return `Financial Year ${selectedFinanceYear}-${selectedFinanceYear + 1}`;
  }, [
    financeFilterType,
    selectedFinanceYear,
    selectedFinanceMonth,
    selectedQuarterIndex,
  ]);

  const filteredPayments = useMemo(
    () =>
      payments.filter((payment) =>
        isDateInSelectedFinanceFilter(
          parseSafeDate(payment.date) || parseSafeDate(payment.createdAt),
        ),
      ),
    [
      payments,
      financeFilterType,
      selectedFinanceYear,
      selectedFinanceMonth,
      selectedQuarterIndex,
    ],
  );

  const filteredVendorPayments = useMemo(
    () =>
      vendorPayments.filter((payment) =>
        isDateInSelectedFinanceFilter(
          parseSafeDate(payment.date) || parseSafeDate(payment.createdAt),
        ),
      ),
    [
      vendorPayments,
      financeFilterType,
      selectedFinanceYear,
      selectedFinanceMonth,
      selectedQuarterIndex,
    ],
  );

  const totalIncome = filteredPayments.reduce(
    (sum, payment) =>
      sum + Number(payment.amount || payment.invoiceAmount || 0),
    0,
  );

  const totalExpense = filteredVendorPayments.reduce(
    (sum, payment) => sum + Number(payment.paymentAmt || 0),
    0,
  );

  const netProfit = totalIncome - totalExpense;

  /* =====================================================
     LEADS & PROJECT COUNTS
     No sales amount or sales chart.
  ===================================================== */

  const totalWonLeads = leads.filter(
    (lead) => lead.status === "Closed - Won",
  ).length;

  const totalLostLeads = leads.filter(
    (lead) => lead.status === "Closed - Lost",
  ).length;

  const totalActiveLeads = leads.filter(
    (lead) => !["Closed - Won", "Closed - Lost"].includes(lead.status),
  ).length;

  const runningProjects = projects.filter(
    (project) => !project.isClosed && !project.isArchived,
  ).length;

  const closedProjects = projects.filter((project) => project.isClosed).length;

  const archivedProjects = projects.filter(
    (project) => project.isArchived,
  ).length;

  /* =====================================================
     ACHIEVED PROJECTS
     Based on Closed - Won leads.
  ===================================================== */

  const achievedProjects = useMemo(
    () =>
      leads
        .filter((lead) => lead.status === "Closed - Won")
        .sort((a, b) => {
          const dateA = getLeadWonDate(a)?.getTime() || 0;

          const dateB = getLeadWonDate(b)?.getTime() || 0;

          return dateB - dateA;
        }),
    [leads],
  );

  const filteredAchievedProjects = achievedProjects.filter((lead) => {
    const query = normalizeText(achievedSearch);

    if (!query) return true;

    return [
      getLeadProjectName(lead),
      getLeadClientName(lead),
      lead?.project?.category,
      lead?.category,
    ].some((value) => normalizeText(value).includes(query));
  });

  /* =====================================================
     PROJECT QUOTATION AND PAYMENT AMOUNTS
  ===================================================== */

  const getProjectFinalAmount = (project) => {
    const projectId = String(project?._id || project?.id || "");

    const projectName = normalizeText(project?.name);

    const projectQuotes = quotes.filter((quote) => {
      const quoteProjectId = String(
        quote?.projectId?._id ||
          quote?.projectId ||
          quote?.project?._id ||
          quote?.project ||
          "",
      );

      const quoteProjectName = normalizeText(
        quote?.projectName || quote?.project?.name,
      );

      return (
        (projectId && quoteProjectId === projectId) ||
        (projectName && quoteProjectName === projectName)
      );
    });

    const approvedQuotes = projectQuotes.filter(
      (quote) => normalizeText(quote.status) === "approved",
    );

    if (approvedQuotes.length) {
      const latestApproved = [...approvedQuotes].sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt || a.date || 0);

        const dateB = new Date(b.updatedAt || b.createdAt || b.date || 0);

        return dateB - dateA;
      })[0];

      return Number(latestApproved?.finalAmount || 0);
    }

    if (projectQuotes.length) return 0;

    return Number(project?.finalAmount || 0);
  };

  const getProjectTotalPaid = (project) => {
    const projectId = String(project?._id || project?.id || "");

    const projectName = normalizeText(project?.name);

    return payments
      .filter((payment) => {
        const paymentProjectId = String(
          payment?.projectId?._id ||
            payment?.projectId ||
            payment?.project?._id ||
            payment?.project ||
            "",
        );

        const paymentProjectName = normalizeText(
          payment?.projectName || payment?.project?.name,
        );

        return (
          (projectId && paymentProjectId === projectId) ||
          (projectName && paymentProjectName === projectName)
        );
      })
      .reduce(
        (sum, payment) =>
          sum + Number(payment.amount || payment.invoiceAmount || 0),
        0,
      );
  };

  const isArchivedProject = (project) =>
    project?.isArchived === true ||
    String(project?.isArchived).toLowerCase() === "true" ||
    normalizeText(project?.status) === "archived";

  const activeProjectCount = projects.filter(
    (project) => !isArchivedProject(project),
  ).length;

  // Keep archived projects in their own section at the very bottom of the page.
  const filteredProjects = projects
    .filter((project) => !isArchivedProject(project))
    .filter((project) =>
      normalizeText(project?.name).includes(normalizeText(searchTerm)),
    )
    .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));

  const archivedProjectList = projects
    .filter(isArchivedProject)
    .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));

  const openProjectByName = (projectName, projectId) => {
    const id = String(projectId?._id || projectId || "").trim();
    const name = normalizeText(projectName);

    const project = projects.find((item) => {
      const itemId = String(item?._id || item?.id || "");
      return (
        (id && itemId === id) || (name && normalizeText(item?.name) === name)
      );
    });

    if (!project) {
      alert(`Project "${projectName || id || "Unknown"}" not found.`);
      return;
    }

    openProject(project);
  };

  /* =====================================================
     VENDOR REQUEST FILTER
  ===================================================== */

  const pendingVendorRequests = vendorPaymentRequests.filter(
    (request) => request.status === "PENDING_APPROVAL",
  );

  const visibleRequests = vendorPaymentRequests.filter(
    (request) => request.status === requestFilter,
  );

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div className="admin-dashboard-layout">
      <AdminSidebar />

      <main className={`admin-container ${darkMode ? "dark" : ""}`}>
        {/* HEADER */}

        <header className="admin-header">
          <div className="admin-header-content">
            <h1 className="admin-title">Admin Dashboard</h1>

            <p className="admin-subtitle">
              Monitor projects, finance, leads and vendor payment approvals.
            </p>
          </div>

          <div className="admin-header-actions">
            <button
              type="button"
              className="admin-theme-btn"
              onClick={() => setDarkMode((previous) => !previous)}
            >
              {darkMode ? "☀ Light Mode" : "☾ Dark Mode"}
            </button>
          </div>
        </header>

        {loadError && (
          <div className="admin-alert" role="alert">
            {loadError}
          </div>
        )}

        {/* FINANCE FILTER */}

        <section className="admin-filter-panel">
          <div className="admin-filter-heading">
            <div>
              <h2>Finance Period</h2>

              <p>Select a period to review financial performance.</p>
            </div>

            <span className="admin-period-badge">{selectedFilterLabel}</span>
          </div>

          <div className="admin-filter-grid">
            <div className="admin-field">
              <label htmlFor="finance-type">Filter Type</label>

              <select
                id="finance-type"
                value={financeFilterType}
                onChange={(event) => setFinanceFilterType(event.target.value)}
              >
                <option value="MONTHLY">Monthly</option>

                <option value="QUARTERLY">Quarterly</option>

                <option value="YEARLY">Yearly</option>
              </select>
            </div>

            <div className="admin-field">
              <label htmlFor="finance-year">Financial Year</label>

              <select
                id="finance-year"
                value={selectedFinanceYear}
                onChange={(event) =>
                  setSelectedFinanceYear(Number(event.target.value))
                }
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}-{year + 1}
                  </option>
                ))}
              </select>
            </div>

            {financeFilterType === "MONTHLY" && (
              <div className="admin-field">
                <label htmlFor="finance-month">Month</label>

                <select
                  id="finance-month"
                  value={selectedFinanceMonth}
                  onChange={(event) =>
                    setSelectedFinanceMonth(Number(event.target.value))
                  }
                >
                  {MONTHS.map((month, index) => (
                    <option key={month} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {financeFilterType === "QUARTERLY" && (
              <div className="admin-field">
                <label htmlFor="finance-quarter">Quarter</label>

                <select
                  id="finance-quarter"
                  value={selectedQuarterIndex}
                  onChange={(event) =>
                    setSelectedQuarterIndex(Number(event.target.value))
                  }
                >
                  {QUARTERS.map((quarter, index) => (
                    <option key={quarter.label} value={index}>
                      {quarter.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </section>

        {/* SUMMARY CARDS */}

        <section className="admin-summary-grid">
          <SummaryCard
            label="Total Projects"
            value={projects.length}
            icon="▦"
            tone="blue"
          />

          <SummaryCard
            label="Running Projects"
            value={runningProjects}
            icon="▶"
            tone="green"
          />

          <SummaryCard
            label="Closed Projects"
            value={closedProjects}
            icon="✓"
            tone="orange"
          />

          <SummaryCard
            label="Archived Projects"
            value={archivedProjects}
            icon="▣"
            tone="purple"
          />

          <SummaryCard
            label="Income"
            value={formatINR(totalIncome)}
            subtitle={selectedFilterLabel}
            icon="↗"
            tone="cyan"
          />

          <SummaryCard
            label="Expense"
            value={formatINR(totalExpense)}
            subtitle={selectedFilterLabel}
            icon="↙"
            tone="orange"
          />

          <SummaryCard
            label="Profit"
            value={formatINR(netProfit)}
            subtitle={selectedFilterLabel}
            icon="₹"
            tone="green"
          />

          <SummaryCard
            label="Pending Vendor Approvals"
            value={pendingVendorRequests.length}
            icon="◷"
            tone="red"
          />
        </section>

        {/* ACHIEVED PROJECTS LINK */}

        {/* FINANCE SUMMARY */}

        <Section title="Finance Summary" subtitle={selectedFilterLabel}>
          <div className="admin-table-scroll">
            <table className="admin-data-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Income</th>
                  <th>Expense</th>
                  <th>Profit</th>
                  <th>Income Entries</th>
                  <th>Expense Entries</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>{selectedFilterLabel}</td>

                  <td className="admin-amount-positive">
                    {formatINR(totalIncome)}
                  </td>

                  <td>{formatINR(totalExpense)}</td>

                  <td
                    className={
                      netProfit >= 0
                        ? "admin-amount-positive"
                        : "admin-amount-negative"
                    }
                  >
                    {formatINR(netProfit)}
                  </td>

                  <td>{filteredPayments.length}</td>

                  <td>{filteredVendorPayments.length}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Section>

        {/* VENDOR PAYMENT APPROVAL */}

        <Section
          title="Vendor Payment Approval Requests"
          subtitle="Review, edit and approve vendor payment requests."
          action={
            <select
              className="admin-section-select"
              value={requestFilter}
              onChange={(event) => setRequestFilter(event.target.value)}
            >
              <option value="PENDING_APPROVAL">Pending Approval</option>

              <option value="APPROVED">Approved</option>

              <option value="REJECTED">Rejected</option>

              <option value="PAID">Paid</option>
            </select>
          }
        >
          <div className="admin-table-scroll">
            <table className="admin-data-table admin-vendor-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Project</th>
                  <th>Vendor Code</th>
                  <th>Vendor Name</th>
                  <th>Work Type</th>
                  <th>Amount</th>
                  <th>Mode</th>
                  <th>GST Invoice</th>
                  <th>Status</th>
                  <th>Created By</th>
                  <th>Remark</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {visibleRequests.map((request) => (
                  <tr key={request._id}>
                    <td>{request.date || "-"}</td>

                    <td>
                      {request.projectName || request.project?.name ? (
                        <button
                          type="button"
                          className="admin-project-open-btn"
                          onClick={() =>
                            openProjectByName(
                              request.projectName || request.project?.name,
                              request.projectId || request.project?._id,
                            )
                          }
                          title={`Open ${request.projectName || request.project?.name}`}
                        >
                          {request.projectName || request.project?.name}
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td>{request.vendorCode || "-"}</td>

                    <td>
                      <strong>{request.vendorName || "-"}</strong>
                    </td>

                    <td>{request.workType || "-"}</td>

                    <td className="admin-payment-cell">
                      {request.status === "PENDING_APPROVAL" ? (
                        <div className="admin-edit-field">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={request.paymentAmt ?? ""}
                            onChange={(event) =>
                              updatePendingRequestField(
                                request._id,
                                "paymentAmt",
                                event.target.value,
                              )
                            }
                          />

                          <small>Admin final amount</small>
                        </div>
                      ) : (
                        <strong>{formatINR(request.paymentAmt || 0)}</strong>
                      )}
                    </td>

                    <td className="admin-payment-cell">
                      {request.status === "PENDING_APPROVAL" ? (
                        <div className="admin-edit-field">
                          <select
                            value={request.mode || ""}
                            onChange={(event) =>
                              updatePendingRequestField(
                                request._id,
                                "mode",
                                event.target.value,
                              )
                            }
                          >
                            {paymentModes.map((mode) => (
                              <option key={mode} value={mode}>
                                {mode}
                              </option>
                            ))}
                          </select>

                          <small>Admin final mode</small>
                        </div>
                      ) : (
                        <strong>{request.mode || "-"}</strong>
                      )}
                    </td>

                    <td>{request.gstInvoice || "NO"}</td>

                    <td>
                      <StatusBadge status={request.status} />
                    </td>

                    <td>{request.createdByName || "-"}</td>

                    <td className="admin-remark-cell">
                      <div className="admin-remark-box">
                        <div className="admin-remark-finance">
                          <div>
                            PO Total:
                            <strong>{formatINR(request.poTotal || 0)}</strong>
                          </div>

                          <div>
                            Paid:
                            <strong>
                              {formatINR(request.paidAmount || 0)}
                            </strong>
                          </div>

                          <div>
                            For Your Approval:
                            <strong>
                              {formatINR(
                                request.status === "PAID"
                                  ? 0
                                  : request.inProcessAmount || 0,
                              )}
                            </strong>
                          </div>

                          <div>
                            Pending Payment:
                            <strong>
                              {formatINR(request.balanceAmount || 0)}
                            </strong>
                          </div>
                        </div>

                        {request.note && (
                          <div className="admin-user-note">
                            <strong>User Note</strong>

                            <p>{request.note}</p>
                          </div>
                        )}

                        {request.status === "PENDING_APPROVAL" && (
                          <div className="admin-admin-remark">
                            <label>Admin Remark</label>

                            <textarea
                              placeholder="Write approval or rejection remark..."
                              value={request.adminRemark || ""}
                              onChange={(event) =>
                                updatePendingRequestField(
                                  request._id,
                                  "adminRemark",
                                  event.target.value,
                                )
                              }
                            />
                          </div>
                        )}
                      </div>
                    </td>

                    <td>
                      {request.status === "PENDING_APPROVAL" ? (
                        <div className="admin-action-buttons">
                          <button
                            type="button"
                            className="admin-approve-btn"
                            onClick={() => approveRequest(request._id)}
                            disabled={actionLoadingId === request._id}
                          >
                            {actionLoadingId === request._id
                              ? "Please wait..."
                              : "Approve"}
                          </button>

                          <button
                            type="button"
                            className="admin-reject-btn"
                            onClick={() => rejectRequest(request._id)}
                            disabled={actionLoadingId === request._id}
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}

                {!visibleRequests.length && (
                  <tr>
                    <td colSpan={12} className="admin-empty-state">
                      No vendor payment requests found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>

        {/* INCOME ENTRIES */}

        <Section title="Income Entries" subtitle={selectedFilterLabel}>
          <div className="admin-table-scroll">
            <table className="admin-data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Project</th>
                  <th>Method</th>
                  <th>Invoice No.</th>
                  <th>Amount</th>
                </tr>
              </thead>

              <tbody>
                {filteredPayments.map((payment) => (
                  <tr key={payment._id}>
                    <td>{formatDate(payment.date || payment.createdAt)}</td>

                    <td>
                      <button
                        type="button"
                        className="admin-project-open-btn"
                        onClick={() =>
                          openProjectByName(
                            payment.projectName || payment.project?.name,
                          )
                        }
                        disabled={
                          !projects.some(
                            (item) =>
                              normalizeText(item?.name) ===
                              normalizeText(
                                payment.projectName || payment.project?.name,
                              ),
                          )
                        }
                      >
                        {payment.projectName || payment.project?.name || "-"}
                      </button>
                    </td>

                    <td>{payment.method || payment.bankName || "-"}</td>

                    <td>{payment.invoiceNo || "-"}</td>

                    <td className="admin-amount-positive">
                      {formatINR(payment.amount || payment.invoiceAmount || 0)}
                    </td>
                  </tr>
                ))}

                {!filteredPayments.length && (
                  <tr>
                    <td colSpan={5} className="admin-empty-state">
                      No income entries found for {selectedFilterLabel}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>

        {/* EXPENSE ENTRIES */}

        <Section title="Expense Entries" subtitle={selectedFilterLabel}>
          <div className="admin-table-scroll">
            <table className="admin-data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Project</th>
                  <th>Vendor</th>
                  <th>Work Type</th>
                  <th>Amount</th>
                </tr>
              </thead>

              <tbody>
                {filteredVendorPayments.map((payment) => (
                  <tr key={payment._id}>
                    <td>{formatDate(payment.date || payment.createdAt)}</td>

                    <td>
                      <button
                        type="button"
                        className="admin-project-open-btn"
                        onClick={() =>
                          openProjectByName(
                            payment.projectName || payment.project?.name,
                          )
                        }
                        disabled={
                          !projects.some(
                            (item) =>
                              normalizeText(item?.name) ===
                              normalizeText(
                                payment.projectName || payment.project?.name,
                              ),
                          )
                        }
                      >
                        {payment.projectName || payment.project?.name || "-"}
                      </button>
                    </td>

                    <td>{payment.vendorName || "-"}</td>

                    <td>{payment.workType || "-"}</td>

                    <td>{formatINR(payment.paymentAmt || 0)}</td>
                  </tr>
                ))}

                {!filteredVendorPayments.length && (
                  <tr>
                    <td colSpan={5} className="admin-empty-state">
                      No expense entries found for {selectedFilterLabel}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>

        {/* PROJECTS OVERVIEW — ARCHIVED PROJECTS LAST */}
        <section className="admin-projects-panel">
          <div className="admin-projects-header">
            <div className="admin-projects-heading">
              <div className="admin-projects-heading-icon">▦</div>
              <div>
                <div className="admin-projects-eyebrow">PROJECT MANAGEMENT</div>
                <h2>Projects Overview</h2>
                <p>
                  Projects (archived last), approved quotation values and
                  received payments.
                </p>
              </div>
            </div>
            <div className="admin-projects-header-right">
              <span className="admin-projects-counter">
                ● {projects.length} Projects
              </span>
              <div className="admin-projects-search">
                <span aria-hidden="true">⌕</span>
                <input
                  type="search"
                  aria-label="Search projects"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="admin-projects-table-wrap">
            <table className="admin-projects-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Approved Value</th>
                  <th>Payment Received</th>
                  <th>Payment Progress</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project) => {
                  const finalAmount = Number(
                    getProjectFinalAmount(project) || 0,
                  );
                  const totalPaid = Number(getProjectTotalPaid(project) || 0);
                  const progress =
                    finalAmount > 0
                      ? Math.min(
                          100,
                          Math.max(0, (totalPaid / finalAmount) * 100),
                        )
                      : 0;
                  const isArchived = isArchivedProject(project);
                  const isClosed =
                    project.isClosed === true ||
                    String(project.isClosed).toLowerCase() === "true" ||
                    normalizeText(project.status) === "closed";
                  return (
                    <tr key={project._id || project.id || project.name}>
                      <td>
                        <button
                          type="button"
                          className="admin-project-open-btn"
                          onClick={() => openProject(project)}
                          title={`Open ${project.name || "project"}`}
                        >
                          {project.name || "Unnamed Project"}
                        </button>
                      </td>
                      <td>
                        <div className="admin-project-money">
                          <strong>{formatINR(finalAmount)}</strong>
                          <small>Approved quotation</small>
                        </div>
                      </td>
                      <td>
                        <div className="admin-project-money admin-project-paid">
                          <strong>{formatINR(totalPaid)}</strong>
                          <small>Received</small>
                        </div>
                      </td>
                      <td>
                        <div className="admin-project-progress">
                          <div className="admin-project-progress-top">
                            <span>Collected</span>
                            <strong>{progress.toFixed(0)}%</strong>
                          </div>
                          <div
                            className="admin-project-progress-track"
                            role="progressbar"
                            aria-label={`Payment progress for ${project.name}`}
                            aria-valuenow={Math.round(progress)}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          >
                            <div
                              className="admin-project-progress-fill"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`admin-project-status ${isArchived ? "admin-project-status-archived" : isClosed ? "admin-project-status-closed" : "admin-project-status-running"}`}
                        >
                          <span className="admin-project-status-dot" />
                          {isArchived
                            ? "Archived"
                            : isClosed
                              ? "Closed"
                              : "Running"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {!filteredProjects.length && (
                  <tr>
                    <td colSpan={5} className="admin-empty-state">
                      {searchTerm
                        ? "No matching projects found."
                        : "No projects found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="admin-projects-footer">
            <span>
              Showing {filteredProjects.length} active/closed projects
            </span>
          </div>
        </section>

        {/* ARCHIVED PROJECTS — SEPARATE SECTION AT PAGE BOTTOM */}
        {/* =====================================================
    ARCHIVED PROJECTS — CLICK TO EXPAND / COLLAPSE
===================================================== */}

        <section
          className="admin-archived-section"
          aria-label="Archived projects"
        >
          <div className="admin-archived-header">
            <div>
              <h2>Archived Projects</h2>

              <p>
                Archived records are kept separately at the bottom of the
                dashboard.
              </p>
            </div>

            <div className="admin-archived-header-actions">
              <span className="admin-archived-count">
                {archivedProjectList.length} archived
              </span>

              <button
                type="button"
                className="admin-archived-toggle-btn"
                onClick={() => setShowArchivedProjects((prev) => !prev)}
                aria-expanded={showArchivedProjects}
                aria-controls="archived-projects-list"
              >
                {showArchivedProjects ? "Hide Projects" : "View Projects"}

                <span aria-hidden="true">
                  {showArchivedProjects ? "↑" : "→"}
                </span>
              </button>
            </div>
          </div>

          {/* Show table only after clicking View Projects */}

          {showArchivedProjects && (
            <div
              id="archived-projects-list"
              className="admin-projects-table-wrap"
            >
              <table className="admin-projects-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Approved Value</th>
                    <th>Payment Received</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {archivedProjectList.map((project) => (
                    <tr key={project._id || project.id || project.name}>
                      <td>
                        <button
                          type="button"
                          className="admin-project-open-btn"
                          onClick={() => openProject(project)}
                        >
                          {project.name || "Unnamed Project"}
                        </button>
                      </td>

                      <td>{formatINR(getProjectFinalAmount(project))}</td>

                      <td>{formatINR(getProjectTotalPaid(project))}</td>

                      <td>
                        <span className="admin-project-status admin-project-status-archived">
                          Archived
                        </span>
                      </td>
                    </tr>
                  ))}

                  {!archivedProjectList.length && (
                    <tr>
                      <td colSpan={4} className="admin-empty-state">
                        No archived projects.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

/* =========================================================
   REUSABLE COMPONENTS
========================================================= */

function SummaryCard({ label, value, subtitle, icon, tone = "blue" }) {
  return (
    <div className={`admin-summary-card admin-tone-${tone}`}>
      <div className="admin-summary-card-top">
        <span className="admin-summary-label">{label}</span>

        <span className="admin-summary-icon">{icon}</span>
      </div>

      <h2 className="admin-summary-value">{value}</h2>

      {subtitle && <p className="admin-summary-subtitle">{subtitle}</p>}
    </div>
  );
}

function Section({ title, subtitle, action, children }) {
  return (
    <section className="admin-section">
      <div className="admin-section-header">
        <div>
          <h2>{title}</h2>

          {subtitle && <p>{subtitle}</p>}
        </div>

        {action && <div className="admin-section-action">{action}</div>}
      </div>

      {children}
    </section>
  );
}

function StatusBadge({ status }) {
  const className =
    status === "APPROVED" || status === "PAID"
      ? "admin-status-success"
      : status === "REJECTED"
        ? "admin-status-danger"
        : "admin-status-warning";

  const label = String(status || "-").replace(/_/g, " ");

  return <span className={`admin-status ${className}`}>{label}</span>;
}
